import {
  VStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  HStack,
  Box,
  Circle,
  Icon,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { CustomStepper } from './CustomStepper';
import { useForm } from 'react-hook-form';
import { renderFields } from './RenderFormFields';
import { handleNext } from './HandleNext';
import { defaultValues } from './defaultValues';
import Button from '../Button';
import { useGetOnboardingUser, usePostOnboardingUser } from '../../../services/dashboard.ts';
import TickIcon from '../../../assets/svg/tick-icon.svg?react';
import CustomButton from '../../../components/common/Button';
import { useJsApiLoader } from '@react-google-maps/api';
import { IUser } from '@/services/auth/types.ts';
interface IOnBoardingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
  user: IUser;
}

const steps = [{ title: 'Company Details' }, { title: 'Store Details' }, { title: 'Bank Details' }];
const GOOGLE_MAPS_LIBRARIES = ['places'] as const satisfies (
  | 'places'
  | 'drawing'
  | 'geometry'
  | 'localContext'
  | 'visualization'
)[];
export function OnBoardingModal({ isOpen, onClose, onComplete, user }: IOnBoardingModalProps) {
  const {
    control,
    handleSubmit,
    getValues,
    setError,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: defaultValues,
  });

  const {
    mutateAsync: onBoardUser,
    isLoading,
    isSuccess,
    reset: resetOnBoardingUser,
  } = usePostOnboardingUser();

  // const {
  //   mutateAsync: patchOnBoardUser,
  //   isLoading: patchLoading,
  //   isSuccess: patchSuccess,
  //   reset: resetPatch,
  // } = usePatchOnboardingUser();

  const { data: vendorData, isLoading: isFetchingData } = useGetOnboardingUser({
    enabled: isOpen && user?.vendor_status?.status === 'rejected', // Fetch only when modal opens for rejected
  });

  const { isLoaded: isGoogleMapsLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '',
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const [page, setPage] = useState(0);
  const max = steps.length - 1;
  // Prefill form with fetched data when available (matches DefaultValues structure)
  useEffect(() => {
    if (isOpen && vendorData && !isFetchingData) {
      // Company Details
      setValue('company_details.name', vendorData.company_details?.name || '');
      setValue('company_details.address', vendorData.company_details?.address || '');
      setValue(
        'company_details.currency_preference',
        vendorData.company_details?.currency_preference || 'USD',
      );
      // File fields: Set as strings (e.g., URLs from backend) or null; form will handle upload
      setValue(
        'company_details.company_certificate',
        vendorData?.company_details?.company_certificate || null,
      );
      setValue(
        'company_details.pan_vat_certificate',
        vendorData.company_details?.pan_vat_certificate || '',
      );

      // Store Details
      setValue('store_details.name', vendorData.store_details?.name || '');
      setValue(
        'store_details.warehouse_address',
        vendorData.store_details?.warehouse_address || '',
      );
      setValue('store_details.contact_number', vendorData.store_details?.contact_number || '');
      setValue('store_details.store_logo', vendorData.store_details?.store_logo || '');

      // Bank Details
      setValue('bank_details.bank_name', vendorData.bank_details?.bank_name || '');
      setValue(
        'bank_details.account_holder_name',
        vendorData.bank_details?.account_holder_name || '',
      );
      setValue('bank_details.branch_code', vendorData.bank_details?.branch_code || '');
      setValue('bank_details.account_number', vendorData.bank_details?.account_number || '');
      setValue(
        'bank_details.bank_cheque_book_scan_photo',
        vendorData.bank_details?.bank_cheque_book_scan_photo || '',
      );
    }
  }, [vendorData, isFetchingData, isOpen, setValue]);

  const handleBack = () => {
    if (page > 0) setPage(page - 1);
  };

  const handleSkip = () => {
    setPage(0);
    onClose();
  };

  const progressPercent = (page / max) * 100;
  return (
    <Modal isOpen={isOpen} onClose={onClose} size={'xl'} isCentered>
      <ModalOverlay />
      <ModalContent maxHeight="90vh">
        {isSuccess ? (
          <Box p={5} fontFamily={'primary'}>
            <VStack gap={6}>
              <Circle bg={'status.success'} maxHeight={'83px'} maxWidth={'83px'} p={3}>
                <Icon as={TickIcon} boxSize={8} />
              </Circle>
              <VStack gap={2}>
                <Text
                  fontSize={'lg'}
                  fontWeight={'semibold'}
                  color={'text.primary'}
                  textAlign={'center'}
                >
                  Onboarding completed successfully!
                </Text>
                <Text textAlign={'center'} fontSize={'xs'} color={'text.secondary'}>
                  Your onboarding is complete. You can start exploring the platform right away while
                  we review and update your verification status, which is currently pending.
                </Text>
              </VStack>
              <CustomButton
                fontSize="md"
                fontWeight="semibold"
                type="button"
                fontFamily={'primary'}
                px={12}
                width={'100%'}
                onClick={() => {
                  onComplete();
                  resetOnBoardingUser();
                }}
              >
                Done
              </CustomButton>
            </VStack>
          </Box>
        ) : (
          <form
            onSubmit={handleSubmit(() =>
              handleNext({
                page,
                max,
                getValues,
                setError,
                setPage,
                onBoardUser,
                isLoading,
              }),
            )}
          >
            <VStack justify={'flex-start'} width="100%">
              <ModalHeader px={4} position="relative">
                <VStack gap={2} align={'start'} width={'90%'}>
                  <Text
                    fontSize={'xl'}
                    fontWeight={'semibold'}
                    color={'text.primary'}
                    fontFamily={'primary'}
                  >
                    Welcome to KurtaSari
                  </Text>
                  <Text fontSize={'md'} color={'text.secondary'} fontFamily={'primary'}>
                    Start selling with us! Fill out your vendor details to set up your account and
                    begin your e-commerce journey.
                  </Text>
                </VStack>
                <Box position="absolute" top={1} right={0}>
                  <ModalCloseButton onClick={onClose} />
                </Box>
              </ModalHeader>
            </VStack>

            <ModalBody bg={'background.secondary'} p={4} maxHeight="60vh" overflowY="auto">
              <VStack width={'100%'} gap={6}>
                <HStack width={'100%'} px={10}>
                  <CustomStepper
                    steps={steps}
                    activeStep={page}
                    progressPercent={progressPercent}
                  />
                </HStack>
                {renderFields({
                  control,
                  errors,
                  page,
                  setValue,
                  isGoogleMapsLoaded,
                  apiLoadError: loadError?.message,
                })}
              </VStack>
            </ModalBody>

            <ModalFooter bg="white">
              <HStack width="100%" justify="space-between">
                <Text
                  as={'button'}
                  fontFamily={'secondary'}
                  fontWeight={'semibold'}
                  color={'brand.primary.DEFAULT'}
                  fontSize={'md'}
                  onClick={handleSkip}
                  cursor={'pointer'}
                >
                  Skip
                </Text>

                <HStack justify="flex-end" spacing={4} width="100%">
                  {page > 0 && (
                    <Button
                      fontSize={'md'}
                      borderColor={'brand.primary.DEFAULT'}
                      fontWeight={'semibold'}
                      onClick={handleBack}
                      bg={'#fff'}
                      color={'brand.primary.DEFAULT'}
                      border={'2px'}
                      fontFamily={'secondary'}
                      _hover={{
                        color: '#fff',
                        border: '2px',
                        borderColor: 'brand.primary.DEFAULT',
                        bg: 'brand.primary.DEFAULT',
                      }}
                    >
                      Back
                    </Button>
                  )}

                  <Button
                    type="submit"
                    variant="primary"
                    fontSize={'md'}
                    fontFamily={'primary'}
                    fontWeight={'semibold'}
                    isLoading={isLoading}
                  >
                    {page === max ? 'Submit' : 'Next'}
                  </Button>
                </HStack>
              </HStack>
            </ModalFooter>
          </form>
        )}
      </ModalContent>
    </Modal>
  );
}
